import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, ArrowLeft, MailCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { API } from '../config/api';

const LOGO = "/assets/logo-puro.png";
const LOGO_FILTER = { filter: 'brightness(0) saturate(100%) invert(45%) sepia(28%) saturate(520%) hue-rotate(357deg) brightness(0.92)' };

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setDone(true);
    } catch (err) {
      const status = err?.response?.status;
      if (!err?.response) {
        setError("Connexion au serveur impossible. Vérifiez votre connexion internet et réessayez.");
      } else if (status === 429) {
        setError("Trop de demandes. Veuillez patienter quelques minutes avant de réessayer.");
      } else {
        const detail = err?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : "Nous n'avons pas pu envoyer l'email. Réessayez dans un instant.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5EFE5] flex flex-col">
      <div className="p-4 sm:p-6">
        <button onClick={() => navigate('/login')} className="inline-flex items-center text-[#7A6455] hover:text-[#C99A55] transition-colors text-sm" data-testid="forgot-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Retour à la connexion
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src={LOGO} alt="PURO" className="h-14 w-auto mx-auto mb-5" style={LOGO_FILTER} />
            <h1 className="font-heading font-medium text-2xl sm:text-3xl text-[#493329] tracking-tight">
              {done ? 'Email envoyé' : 'Mot de passe oublié'}
            </h1>
            <div className="w-8 h-0.5 bg-[#C99A55] mx-auto mt-3" />
          </div>

          {done ? (
            <Card className="border-[#E9DDCC] rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <CardContent className="p-8 text-center">
                <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#C99A55]/15 flex items-center justify-center">
                  <MailCheck className="w-7 h-7 text-[#C99A55]" strokeWidth={1.5} />
                </div>
                <p className="text-[#493329] text-base mb-3 font-medium">Vérifiez votre boîte mail</p>
                <p className="text-sm text-[#7A6455] leading-relaxed mb-6">
                  Si un compte existe avec l'adresse <strong className="text-[#493329]">{email}</strong>, vous recevrez sous peu un email contenant un lien pour réinitialiser votre mot de passe.
                </p>
                <p className="text-xs text-[#AE9885] italic mb-6">
                  Le lien est valable 1 heure. N'oubliez pas de vérifier vos spams.
                </p>
                <Link to="/login">
                  <Button className="bg-[#C99A55] hover:bg-[#493329] text-white rounded-xl h-11 px-8 transition-all duration-300" data-testid="forgot-back-login">
                    Retour à la connexion
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-[#E9DDCC] rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <CardContent className="p-6 sm:p-8">
                <p className="text-sm text-[#7A6455] mb-6 leading-relaxed">
                  Entrez l'adresse email associée à votre compte. Nous vous enverrons un lien sécurisé pour créer un nouveau mot de passe.
                </p>
                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <Alert variant="destructive" className="bg-[#C76542]/10 border-[#C76542]/20 text-[#C76542]">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#493329] text-sm">Email</Label>
                    <Input
                      id="email" type="email" required
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      className="h-12 rounded-xl border-[#E9DDCC] bg-white"
                      placeholder="vous@exemple.com"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#C99A55] hover:bg-[#493329] text-white h-12 rounded-xl btn-premium transition-all duration-400 text-base"
                    disabled={loading}
                    data-testid="forgot-submit"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer le lien de réinitialisation"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-[#7A6455] hover:text-[#C99A55] transition-colors">
              Vous vous souvenez ? Connectez-vous
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
